/* Latest package selection for the Swedish and Norwegian storefronts. */
(() => {
  'use strict';
  const market = (document.documentElement.dataset.nllMarket || '').toLowerCase();
  const sweden = /(^|\.)[^.]+\.se$/i.test(location.hostname) || market === 'se';
  const norway = /^(www\.)?nordiclongevitylabs\.no$/i.test(location.hostname) || market === 'no';
  if (!sweden && !norway) return;
  if (window.__nllSeCartGuard) return;
  window.__nllSeCartGuard = true;

  const packs = new Map(sweden ? [
    ['50753700593944', 1],
    ['50753703182616', 2],
    ['50753702363416', 3]
  ] : [
    ['50752878969112', 1],
    ['50689383530776', 2],
    ['50753026523416', 3],
    ['50756345102616', 6]
  ]);
  const limitMessage = sweden
    ? 'Välj ett paket med 1, 2 eller 3 påsar. Ditt senaste paketval ersätter det tidigare i varukorgen.'
    : 'Velg én pakke med 1, 2, 3 eller 6 poser. Ditt siste pakkevalg erstatter det tidligere i handlekurven.';
  const checkMessage = sweden
    ? 'Vi kunde inte kontrollera varukorgen. Försök igen innan du går vidare.'
    : 'Vi kunne ikke oppdatere handlekurven. Prøv igjen før du går videre.';
  const nativeFetch = window.fetch.bind(window);
  let queue = Promise.resolve();
  const enqueue = (work) => {
    const result = queue.then(work, work);
    queue = result.then(() => undefined, () => undefined);
    return result;
  };
  const quantity = (value, fallback) => {
    const number = value === undefined || value === null ? fallback : Number(value);
    if (!Number.isSafeInteger(number) || number < 0) throw new Error(checkMessage);
    return number;
  };
  const cartPath = () => {
    const root = window.Shopify?.routes?.root || '/';
    return new URL(root.replace(/\/?$/, '/') + 'cart.js', location.origin).href;
  };
  const cartItems = (cart) => {
    if (!Array.isArray(cart.items)) throw new Error(checkMessage);
    return cart.items.map((item) => ({
      id: String(item.variant_id || item.id),
      key: String(item.key || item.id),
      quantity: quantity(item.quantity, 0),
      plan: String(item.selling_plan_allocation?.selling_plan?.id || ''),
      properties: item.properties || {},
      raw: item
    }));
  };
  const readCart = async () => {
    const response = await nativeFetch(cartPath(), { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(checkMessage);
    const cart = await response.json();
    return cartItems(cart);
  };
  const isValid = (items) => {
    if (!sweden) return items.filter(item => packs.has(item.id)).reduce((total, item) => total + item.quantity, 0) <= 1;
    let total = 0;
    for (const item of items) {
      if (!item.quantity) continue;
      if (!packs.has(item.id)) return false;
      total += packs.get(item.id) * item.quantity;
    }
    return total <= 3;
  };
  const reject = (message) => new Response(JSON.stringify({ status: 422, message, description: message, errors: message }), {
    status: 422,
    headers: { 'Content-Type': 'application/json; charset=utf-8' }
  });
  const parseForm = (form) => {
    const result = {};
    const items = [];
    let updates;
    for (const [name, value] of form.entries()) {
      if (typeof value !== 'string') { result.__nllFile = true; continue; }
      let match;
      if ((match = name.match(/^items\[(\d+)\]\[(id|quantity|selling_plan)\]$/))) {
        items[Number(match[1])] ||= {};
        items[Number(match[1])][match[2]] = value;
      } else if ((match = name.match(/^items\[(\d+)\]\[properties\]\[([^\]]+)\]$/))) {
        items[Number(match[1])] ||= {};
        (items[Number(match[1])].properties ||= {})[match[2]] = value;
      } else if ((match = name.match(/^properties\[([^\]]+)\]$/))) {
        (result.properties ||= {})[match[1]] = value;
      } else if (name === 'updates[]') {
        if (updates && !Array.isArray(updates)) throw new Error(checkMessage);
        (updates ||= []).push(value);
      } else if ((match = name.match(/^updates\[([^\]]+)\]$/))) {
        if (Array.isArray(updates)) throw new Error(checkMessage);
        (updates ||= Object.create(null))[match[1]] = value;
      } else {
        result[name] = value;
      }
    }
    if (items.length) result.items = items.filter(Boolean);
    if (updates) result.updates = updates;
    return result;
  };
  const parseBody = async (request) => {
    const type = request.headers.get('content-type') || '';
    if (type.includes('multipart/form-data') || type.includes('application/x-www-form-urlencoded')) {
      return parseForm(await request.clone().formData());
    }
    const body = await request.clone().text();
    if (!body) return {};
    try { return JSON.parse(body); }
    catch (error) {
      if (type.includes('application/json')) throw error;
      return parseForm(new URLSearchParams(body));
    }
  };
  const findLine = (items, value) => {
    const id = String(value);
    const byKey = items.findIndex((item) => item.key === id);
    if (byKey >= 0) return byKey;
    const matches = items.map((item, index) => item.id === id ? index : -1).filter((index) => index >= 0);
    // With selling plans/properties, callers should use the unambiguous line-item key.
    if (matches.length > 1) throw new Error(checkMessage);
    return matches[0] ?? -1;
  };
  const project = (action, data, current) => {
    if (!data || typeof data !== 'object' || Array.isArray(data)) throw new Error(checkMessage);
    const next = current.map((item) => ({ ...item }));
    if (action === 'add') {
      const additions = Array.isArray(data.items) ? data.items : [data];
      for (const item of additions) {
        const id = String(item.id);
        if (!packs.has(id)) throw new Error(limitMessage);
        next.push({ id, key: '', quantity: quantity(item.quantity, 1) });
      }
    } else if (action === 'change') {
      const index = data.line !== undefined ? quantity(data.line, 0) - 1 : findLine(current, data.id);
      if (index < 0 || index >= current.length) throw new Error(checkMessage);
      if (data.quantity !== undefined) next[index].quantity = quantity(data.quantity);
    } else if (data.updates !== undefined) {
      const updates = data.updates;
      if (Array.isArray(updates)) {
        if (updates.length !== current.length) throw new Error(checkMessage);
        updates.forEach((value, index) => { next[index].quantity = quantity(value); });
      } else if (updates && typeof updates === 'object') {
        for (const [id, value] of Object.entries(updates)) {
          const index = findLine(current, id);
          const amount = quantity(value);
          if (index >= 0) next[index].quantity = amount;
          else if (amount > 0) {
            if (sweden && !packs.has(id)) throw new Error(limitMessage);
            next.push({ id, key: '', quantity: amount });
          }
        }
      } else throw new Error(checkMessage);
    }
    return next;
  };

  const identity = (item) => JSON.stringify([
    item.id,
    item.plan || '',
    Object.entries(item.properties || {}).filter(([, value]) => value !== '' && value != null)
      .map(([key, value]) => [key, String(value)]).sort(([a], [b]) => a.localeCompare(b))
  ]);
  const updateCart = async (updates, data = {}) => {
    const response = await nativeFetch(cartPath().replace(/cart\.js$/, 'cart/update.js'), {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates, sections: data.sections, sections_url: data.sections_url })
    });
    const result = await response.json();
    if (!response.ok || result.errors) throw new Error(checkMessage);
    cartItems(result);
    return result;
  };
  const restoreQuantities = async (before) => {
    let current = await readCart();
    const remaining = new Map();
    before.filter(item => packs.has(item.id)).forEach(item => {
      const key = identity(item);
      remaining.set(key, (remaining.get(key) || 0) + item.quantity);
    });
    const missing = Array.from(remaining).filter(([key, amount]) => amount > 0 && !current.some(item => identity(item) === key));
    if (missing.length) {
      const items = missing.map(([key, amount]) => {
        const item = before.find(candidate => identity(candidate) === key);
        return { id: item.id, quantity: amount, properties: item.properties, ...(item.plan ? { selling_plan: item.plan } : {}) };
      });
      const response = await nativeFetch(cartPath().replace(/cart\.js$/, 'cart/add.js'), {
        method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items })
      });
      const result = await response.json();
      if (!response.ok || result.status || result.errors) throw new Error(checkMessage);
      current = await readCart();
    }
    const updates = {};
    current.filter(item => packs.has(item.id)).forEach(item => {
      const key = identity(item);
      updates[item.key] = remaining.get(key) || 0;
      remaining.delete(key);
    });
    if (Object.keys(updates).length) await updateCart(updates);
  };
  const normalizeAdd = async (request, data) => {
    const headers = new Headers(request.headers);
    const type = headers.get('content-type') || '';
    let body;
    if (type.includes('multipart/form-data') || type.includes('application/x-www-form-urlencoded')) {
      body = await request.clone().formData();
      const itemKey = Array.from(body.keys()).find(key => /^items\[\d+\]\[id\]$/.test(key));
      body.set(itemKey ? itemKey.replace(/\[id\]$/, '[quantity]') : 'quantity', '1');
      headers.delete('content-type');
    } else {
      const normalized = Array.isArray(data.items)
        ? { ...data, items: data.items.map(item => ({ ...item, quantity: 1 })) }
        : { ...data, quantity: 1 };
      body = JSON.stringify(normalized);
      headers.set('content-type', 'application/json');
    }
    return new Request(request, { headers, body });
  };
  const finalSections = async (cart, data) => {
    if (!data.sections) return cart.sections;
    const names = Array.isArray(data.sections) ? data.sections : String(data.sections).split(',');
    if (names.every(name => typeof cart.sections?.[name] === 'string')) return cart.sections;
    const url = new URL(data.sections_url || location.pathname, location.origin);
    if (url.origin !== location.origin) throw new Error(checkMessage);
    url.searchParams.set('sections', names.join(','));
    const response = await nativeFetch(url.href, { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) throw new Error(checkMessage);
    const sections = await response.json();
    if (!names.every(name => typeof sections[name] === 'string')) throw new Error(checkMessage);
    return sections;
  };
  const replacePackage = async (request, data, before) => {
    const additions = Array.isArray(data.items) ? data.items : [data];
    if (additions.length !== 1 || !packs.has(String(additions[0].id))) return reject(limitMessage);
    if (sweden && before.some(item => item.quantity && !packs.has(item.id))) return reject(limitMessage);
    const chosen = additions[0];
    const desired = { id: String(chosen.id), plan: String(chosen.selling_plan || ''), properties: chosen.properties || {} };
    let selected = !data.__nllFile && before.find(item => identity(item) === identity(desired));
    let mutated = false;
    let committed = false;
    try {
      if (!selected) {
        const normalized = await normalizeAdd(request, data);
        mutated = true;
        const response = await nativeFetch(normalized);
        const result = await response.clone().json();
        if (!response.ok || result.status || result.errors) {
          await restoreQuantities(before);
          return response;
        }
        const added = Array.isArray(result.items) ? result.items[0] : result;
        selected = cartItems({ items: [added] })[0];
      }
      const current = await readCart();
      const candidates = current.filter(item => identity(item) === identity(selected));
      selected = candidates.find(item => item.key === selected.key) || (candidates.length === 1 ? candidates[0] : null);
      if (!selected) throw new Error(checkMessage);
      const updates = {};
      current.filter(item => packs.has(item.id)).forEach(item => { updates[item.key] = item.key === selected.key ? 1 : 0; });
      mutated = true;
      const finalCart = await updateCart(updates, data);
      const items = cartItems(finalCart);
      const finalItem = items.find(item => identity(item) === identity(selected));
      if (!finalItem || finalItem.quantity !== 1 || items.filter(item => packs.has(item.id) && item.quantity > 0).length !== 1 || !isValid(items)) throw new Error(checkMessage);
      committed = true;
      const sections = await finalSections(finalCart, data);
      const result = Array.isArray(data.items) ? { items: [finalItem.raw], sections } : { ...finalItem.raw, sections };
      return new Response(JSON.stringify(result), { status: 200, headers: { 'Content-Type': 'application/json' } });
    } catch (_) {
      if (mutated && !committed) { try { await restoreQuantities(before); } catch (_) {} }
      return reject(checkMessage);
    }
  };

  window.fetch = function(input, init) {
    let url;
    try { url = new URL(input instanceof Request ? input.url : input, location.href); }
    catch (_) { return nativeFetch(input, init); }
    const action = url.pathname.match(/\/cart\/(add|change|update|clear)(?:\.js)?\/?$/)?.[1];
    const method = String(init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
    if (url.origin !== location.origin || !action || method !== 'POST') return nativeFetch(input, init);
    // Capture the body immediately, but enqueue validation before any asynchronous work.
    let request;
    try { request = new Request(input instanceof Request ? input : url.href, init); }
    catch (error) { return Promise.reject(error); }
    return enqueue(async () => {
      try {
        if (action === 'clear') {
          const response = await nativeFetch(request);
          await response.clone().arrayBuffer();
          return response;
        }
        const data = await parseBody(request);
        const current = await readCart();
        if (action === 'add') {
          const additions = Array.isArray(data.items) ? data.items : [data];
          if (sweden || additions.some(item => packs.has(String(item.id)))) return await replacePackage(request, data, current);
          const response = await nativeFetch(request);
          await response.clone().arrayBuffer();
          return response;
        }
        const next = project(action, data, current);
        const onlyReduces = action !== 'add' && next.length === current.length &&
          next.every((item, index) => item.id === current[index].id && item.quantity <= current[index].quantity);
        if (!isValid(next) && !onlyReduces) return reject(limitMessage);
      } catch (error) {
        return reject(error.message === limitMessage ? limitMessage : checkMessage);
      }
      const response = await nativeFetch(request);
      // Complete each response before the next cart read, including concurrent add clicks.
      await response.clone().arrayBuffer();
      return response;
    });
  };

  const showMessage = (message) => {
    let element = document.getElementById('nll-se-cart-limit-message');
    if (!element) {
      element = document.createElement('div');
      element.id = 'nll-se-cart-limit-message';
      element.setAttribute('role', 'alert');
      element.setAttribute('tabindex', '-1');
      element.style.cssText = 'padding:16px;margin:12px 0;border:1px solid #8c4545;border-radius:8px;background:#fff4f1;color:#692d2d;line-height:1.5;';
      const target = document.querySelector('#cart-footer-contents, #cart, main') || document.body;
      target.prepend(element);
    }
    element.textContent = message;
    element.focus({ preventScroll: true });
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };
  const checkCheckout = (resume) => enqueue(async () => {
    try {
      if (!isValid(await readCart())) { showMessage(limitMessage); return; }
      document.getElementById('nll-se-cart-limit-message')?.remove();
      resume();
    } catch (_) { showMessage(checkMessage); }
  });
  const isCheckoutUrl = (value) => {
    try {
      const url = new URL(value, location.href);
      return url.origin === location.origin && /\/checkouts?(?:\/|$)/.test(url.pathname);
    } catch (_) { return false; }
  };
  const approvedForms = new WeakSet();
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (approvedForms.has(form)) { approvedForms.delete(form); return; }
    const submitter = event.submitter;
    const isCheckout = isCheckoutUrl(submitter?.getAttribute('formaction') || form.action) ||
      submitter?.name === 'checkout' || submitter?.matches('.js-cart-checkout-button') ||
      (!submitter && /\/cart\/?$/.test(new URL(form.action, location.href).pathname) && form.querySelector('[name="checkout"]'));
    if (!isCheckout) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    checkCheckout(() => {
      approvedForms.add(form);
      try {
        if (submitter && submitter.form === form) form.requestSubmit(submitter);
        else form.requestSubmit();
      } finally { approvedForms.delete(form); }
    });
  }, true);
  const approvedLinks = new WeakSet();
  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('a[href]');
    if (!link || !isCheckoutUrl(link.href)) return;
    if (approvedLinks.has(link)) { approvedLinks.delete(link); return; }
    event.preventDefault();
    event.stopImmediatePropagation();
    checkCheckout(() => {
      approvedLinks.add(link);
      try { link.click(); }
      finally { approvedLinks.delete(link); }
    });
  }, true);
})();

